/* eslint-disable react-hooks/exhaustive-deps */
import { IOptions } from 'common/reactSelectModels';
import { DispensingOrdersService } from 'components/DispensingOrders/DispensingOrders.service';
import { Car, Carrier, Driver } from 'components/DispensingOrders/models';
import { CreateSaleOrderRequest, SaleOrder, SaleOrderCustomer, SaleOrderProduct, SaleOrderStatus } from 'models/sale-order';
import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { AuthenticationContext, CustomerPermission } from 'services/context/authentication.context';
import SaleOrderService from 'services/sale-order.service';

import car from '../../../assets/gfx/order-car.svg';
import carDark from '../../../assets/gfx/order-car-dark.svg';

import list from '../../../assets/gfx/quote-list.svg';
import listDark from '../../../assets/gfx/quote-list-dark.svg';

import SaleOrderConfirm from './SaleOrderConfirm';
import SaleOrderFuelTable from './SaleOrderFuelTable';
import SaleOrderProgressBar, { SaleOrderProgressStep } from './SaleOrderProgressBar';
import SaleOrderStatusBar from './SaleOrderStatusBar';
import SaleOrderTransportForm, { TransportForm, PAYMENT_DEADLINES } from './SaleOrderTransportForm';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import { isoDateFormat } from 'common/dateFormatterConfig';
import { isValidEmail } from 'helpers/validators';

enum GTMEventType {
  QUOTE_CHECKOUT = 'checkout_lead',
  QUOTE_CREATE = 'generate_lead',
  ORDER_CHECKOUT = 'purchase_checkout',
  ORDER_CREATE = 'purchase'
}

export interface SaleOrderModalProps {
  handleModalHide: () => void;
  onSubmitSuccess: () => void;
  type: 'ORDER' | 'QUOTE',
  isNew: boolean,
  order: SaleOrder | null
}

const MIN_FUEL_AMOUNT = 1000;

export default function SaleOrderModal(props: SaleOrderModalProps) {
  const { state, hasPermission } = useContext(AuthenticationContext);

  const [activeStep, setActiveStep] = useState(props.isNew ? SaleOrderProgressStep.SELECT_TRANSPORT : SaleOrderProgressStep.CONFIRM);
  const [transportForm, setTransportForm] = useState<TransportForm>({
    isOwnTransport: false,
    terminal: null,
    driver: null,
    car: null,
    carrier: null,
    date: null,
    dropoffLocation: null,
    description: null,
    notificationReceiverEmail: null,
    paymentDeadline: state.customer?.defaultPaymentDeadline !== null && state.customer?.defaultPaymentDeadline !== undefined ?
      PAYMENT_DEADLINES.find(d => d.value === state.customer.defaultPaymentDeadline + '') : null
  });

  const [sendEmailNotification, setSendEmailNotification] = useState(false);
  const [carriers, setCarriers] = useState<IOptions[]>([]);
  const [cars, setCars] = useState<IOptions[]>([]);
  const [drivers, setDrivers] = useState<IOptions[]>([]);
  const [products, setProducts] = useState<SaleOrderProduct[]>([]);
  const [productsUpdated, setProductsUpdated] = useState<any>(false);
  const [providers, setProviders] = useState<IOptions[]>([]);
  const [customer, setCustomer] = useState<SaleOrderCustomer>();

  const [isLoading, setIsLoading] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [volumeTimeout, setVolumeTimeout] = useState<any>(null);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    pushEventToDataLayer(SaleOrderProgressStep.SELECT_TRANSPORT, 1);
    if (state.customer) {
      getCustomer(state.customerId as number, setCustomer, setIsLoading);
      getProviders(setProviders, setIsLoading);
      getCarriers(state.customer.searchCode, setCarriers, setCars, setDrivers, transportForm, setTransportForm, setIsLoading);
      setTransportForm({ ...transportForm, notificationReceiverEmail: state.customer.email });
    }
  }, [state.customer]);

  useEffect(() => {
    if (!transportForm.isOwnTransport) {
      const provider = providers.find((p: any) => p.searchCode === 'TT');
      if (provider) {
        getProducts(parseInt(provider.value as string), setProducts);
      }
    } else if (transportForm.terminal?.value) {
      getProducts(transportForm.terminal.value, setProducts)
    }
  }, [transportForm.terminal, transportForm.isOwnTransport, providers])
  
  useEffect(() => {
    if (productsUpdated) {
      if (volumeTimeout) {
        clearTimeout(volumeTimeout);
      }    
      setVolumeTimeout(setTimeout(() => setRoundFuelAmountForProduct(), 500));
    }
  }, [productsUpdated]);

  const previousStep = () => {
    if (activeStep === SaleOrderProgressStep.SELECT_FUEL) {
      setActiveStep(SaleOrderProgressStep.SELECT_TRANSPORT);
    } else if (activeStep === SaleOrderProgressStep.CONFIRM) {
      setActiveStep(SaleOrderProgressStep.SELECT_FUEL);
    } else {
      props.handleModalHide();
    }
  }

  const nextStep = () => {
    if (activeStep === SaleOrderProgressStep.SELECT_TRANSPORT ? !isFormValid() : !areProductsValid()) {
      setTransportForm({ ...transportForm, validated: true, invalid: true });
      return;
    }
    setTransportForm({ ...transportForm, validated: false, invalid: false });
    if (activeStep === SaleOrderProgressStep.SELECT_TRANSPORT) {
      setActiveStep(SaleOrderProgressStep.SELECT_FUEL);
      pushEventToDataLayer(SaleOrderProgressStep.SELECT_FUEL, 2);
    } else if (activeStep === SaleOrderProgressStep.SELECT_FUEL) {
      setActiveStep(SaleOrderProgressStep.CONFIRM);
      pushEventToDataLayer(SaleOrderProgressStep.CONFIRM, 3);
    } else {
      create();
    }
  }

  const create = () => {
    if (sendEmailNotification && (!transportForm.notificationReceiverEmail || !isValidEmail(transportForm.notificationReceiverEmail))) {
      setTransportForm({ ...transportForm, validated: true, invalid: true });
      return;
    }

    const defaultProvider = providers.find(p => p.searchCode === 'TT');
    const request: CreateSaleOrderRequest = {
      customerLocationId: transportForm.dropoffLocation?.value,
      loadingProvider: transportForm.terminal?.value,
      objectId: transportForm.terminal?.objectId || defaultProvider?.objectId,
      carrier: transportForm.carrier?.value || 'TT',
      vehicleCode: transportForm.car?.value,
      driver: transportForm.driver?.value,
      deliveryDate: moment(transportForm.date).format(isoDateFormat),
      notes: transportForm.description,
      paymentDeadline: transportForm.paymentDeadline?.value,
      notificationReceiverEmail: sendEmailNotification ? transportForm.notificationReceiverEmail as string : undefined,
      rows: products.filter(p => p.selected && p.fuelAmount).map(p => ({ batchAmount: p.fuelAmount as number, remoteBatchId: p.batchId, specialFuel: p.specialFuel }))
    };
    if (props.type === 'QUOTE') {
      request.stateId = SaleOrderStatus.QUOTE_IN_PROGRESS;
    }

    setIsRequesting(true);
    SaleOrderService.createSaleOrder(state.customerId as number, request, state.customer!.contactId).then(_ => {
      pushEventToDataLayer(SaleOrderProgressStep.CONFIRM, 4);

      setIsRequesting(false);
      props.onSubmitSuccess();
    }).finally(() => setIsRequesting(false));
  }

  const isFormValid = () => {
    if (transportForm.isOwnTransport) {
      return transportForm.terminal?.value &&
        transportForm.carrier?.value &&
        transportForm.car?.value &&
        transportForm.dropoffLocation?.value &&
        transportForm.date &&
        transportForm.paymentDeadline?.value &&
        (transportForm.description ?? "").length <= 250
    } else {
      return transportForm.dropoffLocation?.value &&
        transportForm.date &&
        transportForm.paymentDeadline?.value &&
        (transportForm.description ?? "").length <= 250
    }
  }

  const areProductsValid = () => {
    return products.filter(p => p.selected).length &&
      !products.find(p => p.selected && validateProduct(p));
  }

  const validateProduct = (product: SaleOrderProduct): boolean => {
    return !product.fuelAmount || product.fuelAmount < MIN_FUEL_AMOUNT;
  }

  const cancel = () => {
    setIsRequesting(true);
    SaleOrderService.cancelSaleOrder(state.customerId as number, props.order?.uid as string, state.customer!.contactId).then(() => {
      props.onSubmitSuccess();      
    }).finally(() => {
      setIsCanceling(false);
      setIsRequesting(false);
    });
  }

  const accept = () => {
    setIsRequesting(true);
    SaleOrderService.acceptSaleOrder(state.customerId as number, props.order?.uid as string, props.order?.validUntil === '0', state.customer!.contactId).then(() => {
      props.onSubmitSuccess();      
    }).finally(() => {
      setIsCanceling(false);
      setIsRequesting(false);
    });
  }

  const setFuelVolumeForProduct = (item: any, volume: any) => {
    if (!products.length) return;
    setProductsUpdated(new Date().getTime());
    setProducts(products.map(i => i.productId === item.productId ? {
      ...i, fuelAmount: volume, invalid: validateProduct({ ...i, fuelAmount: volume }) } : i));
  }

  const setRoundFuelAmountForProduct = () => {
    if (!products.length) return;
    setProducts(products.map(i => ({
      ...i,
      fuelAmount: i.fuelAmount && i.fuelAmount > 1000 ? Math.round(i.fuelAmount / 100) * 100 : i.fuelAmount
    })
    ));
  }

  const pushEventToDataLayer = (step: SaleOrderProgressStep, stepNumber: number) => {
    const getStepName = () => {
      switch(step) {
        case SaleOrderProgressStep.SELECT_TRANSPORT:
          return 'Vali transport';
        case SaleOrderProgressStep.SELECT_FUEL:
          return 'Vali kütus';
        default:
          return stepNumber === 4 ? 'Saadetud' : 'Kinnita';
      }
    }


    if (process.env.REACT_APP_ENV === 'production') {
      window.dataLayer.push({
        event: props.type === 'ORDER' ? GTMEventType.ORDER_CHECKOUT : GTMEventType.QUOTE_CHECKOUT,
        user_company: state.customer.name,
        content_name: getStepName(),
        content_step: stepNumber
      });

      if (stepNumber === 4) {
        if (props.type === 'QUOTE') {
          window.dataLayer.push({
            event: GTMEventType.QUOTE_CREATE,
            user_company: state.customer.name,
            content_name: "Saadetud",
            content_step: stepNumber
          });
        } else {
          window.dataLayer.push({
            event: GTMEventType.ORDER_CREATE,
            user_company: state.customer.name,
            content_name: "Saadetud",
            content_step: stepNumber,
            ecommerce: !!props.order ? {
              value: props.order.rows.reduce((sum, a) => sum + (a.price || 0), 0),
              currency: 'EUR'
            } : null
          });
        }
      }
    }
  }

  let headerImg;
  if (props.isNew) {
    headerImg = props.type === 'ORDER' ? car : list;
  } else {
    headerImg = props.type === 'ORDER' ? carDark : listDark;
  }

  return (
    <Modal
      show={true}
      size={"lg"}
      backdrop='static'
      dialogClassName={props.isNew ? 'modal-dark-header' : ''}
      onHide={props.handleModalHide}
    >
      <Modal.Header closeButton className='position-relative pb-4'>
        <div className="d-flex" style={{ zIndex: 1 }}>
          <img src={headerImg} alt="" className={'mr-3' + (props.isNew ? '' : ' mt-n2')} />
          <div className='d-flex flex-column'>
            {props.isNew &&
            <Modal.Title>
              { props.type === 'QUOTE' ? 'Küsi hinnapakkumist' : 'Uus tellimus'}
            </Modal.Title>
            }
            {!props.isNew &&
            <Modal.Title>
              { props.type === 'QUOTE' ? 'Pakkumine' : 'Tellimus'} { props.order?.docNumber || props.order?.uid }
            </Modal.Title>
            }            
            {
              false && props.isNew && <span className='text-white'>Antud pakkumised kehtivad kuni: 21.12.2022</span>
            }
          </div>
        </div>
        
      </Modal.Header>

      <Modal.Body>
        {
          props.isNew ? <SaleOrderProgressBar activeStep={activeStep} /> :
          <SaleOrderStatusBar order={props.order as SaleOrder} />
        }
        {
          activeStep === SaleOrderProgressStep.SELECT_FUEL ?
          isLoading ? <div className="w-100 d-flex justify-content-center mt-2"><Spinner animation="grow" /></div> : 
          <SaleOrderFuelTable
            items={products}
            toggleItem={(item) => setProducts(products.map(i => i.productId === item.productId ? {...i, selected: !i.selected} : i))}
            changeVolume={(item, volume) => setFuelVolumeForProduct(item, volume)}
            form={transportForm}
          /> : null
        }
        {
          activeStep === SaleOrderProgressStep.SELECT_TRANSPORT ?
          <SaleOrderTransportForm
            state={state}
            form={transportForm}
            setForm={(form) => {
              if (transportForm.carrier !== form.carrier && form.carrier) {
                setCars([]);
                setDrivers([]);
                getCars(form.carrier.value, setCars, setIsLoading);
                getDrivers(form.carrier.value, setDrivers, setIsLoading);
                setTransportForm({...form, car: null, driver: null});
              } else {
                setTransportForm(form);
              }
            }}
            cars={cars}
            drivers={drivers}
            carriers={carriers}
            providers={providers}
            customer={customer}
            isLoading={isLoading}
          /> : null
        }
        {
          activeStep === SaleOrderProgressStep.CONFIRM &&
          <SaleOrderConfirm 
            items={products.filter(p => p.selected && p.fuelAmount)}
            carriers={carriers}
            loadingProviders={providers}
            form={transportForm}
            setForm={setTransportForm}
            order={props.order}
            isNew={props.isNew}
            isOwnTransport={props.isNew ? transportForm.isOwnTransport : props.order?.transporterCode !== 'TT'}
            sendEmailNotification={sendEmailNotification}
            setSendEmailNotification={setSendEmailNotification}
            isRequesting={isRequesting}
          />
        }
      </Modal.Body>
      {props.isNew &&
      <Modal.Footer className='d-flex justify-content-between align-items-center px-4 mx-n4'>
        <button type="button" className="btn btn-outline-dark" onClick={previousStep}>{activeStep === SaleOrderProgressStep.SELECT_TRANSPORT ? 'Loobu' : 'Tagasi'}</button>
        { !isRequesting && activeStep !== SaleOrderProgressStep.CONFIRM && <button type="button" className="btn btn-primary"
          onClick={nextStep}>
          Edasi
        </button>}
        { !isRequesting && activeStep === SaleOrderProgressStep.CONFIRM &&
          <button type="button" className="btn btn-primary" onClick={create}>
            {props.type === 'ORDER' ? 'Kinnita tellimus' : 'Saada'}
          </button>
        }
        {
          isRequesting &&
          <button type="button" className={'btn btn-primary'} disabled>
              <span className='px-2'>Salvestab…</span>
              <Spinner size={"sm"} animation={"border"} />
          </button>
        }
      </Modal.Footer>}
      {!props.isNew && !isCanceling &&
      <Modal.Footer className='d-flex justify-content-end align-items-center px-4 mx-n4'>
        {!isRequesting && props.order?.canChange && hasPermission(CustomerPermission.WEB_ADMIN) && 
          <button type="button" className="btn btn-outline-dark mr-2" onClick={() => setIsCanceling(true)}>Tühista</button>
        }
        {
          !isRequesting && props.type === 'QUOTE' && props.order?.canChange && props.order?.docState === SaleOrderStatus.QUOTE_SUBMITTED && hasPermission(CustomerPermission.WEB_ADMIN) && (
            <button type="button" className="btn btn-primary" onClick={accept}>
              Esita tellimus
            </button>
          )
        }
        {
            isRequesting &&
            <button type="button" className={'btn btn-primary'}>
                <span className='px-2'>Salvestab…</span>
                <Spinner size={"sm"} animation={"border"} />
            </button>
        }        
        {false && !isRequesting && props.type === 'ORDER' && <button type="button" className="btn btn-outline-dark">
          Muuda
        </button>}
      </Modal.Footer>}
      {!props.isNew && isCanceling &&
      <Modal.Footer className='d-flex justify-content-end align-items-center px-4 mx-n4 pb-4 mb-n3 bg-danger'>
        <button type="button" className="btn btn-text text-white mr-2" disabled={isRequesting} onClick={() => setIsCanceling(false)}>Loobu</button>
        {!isRequesting && <button type="button" className="btn btn-white text-danger" onClick={() => cancel()}>
          Kinnita
        </button>}
        {
            isRequesting &&
            <button type="button" className="btn btn-white text-danger" disabled>
                <span className='px-2'>Salvestab…</span>
                <Spinner size={"sm"} animation={"border"} />
            </button>
        }
      </Modal.Footer>}
    </Modal>
  )
}

const getDrivers = (customerId: number, setDrivers: any, setIsLoading: any) => {
  setIsLoading(true);
  DispensingOrdersService.getDrivers(customerId).then((response: Array<Driver>) => {
    setIsLoading(false);
    setDrivers(!response ? [] : response.sort((a, b) => (a.name < b.name) ? -1 : ((a.name > b.name) ? 1 : 0)).map(d => ({ value: d.code, label: d.name })));
  });
};

const getCars = (customerId: number, setCars: any, setIsLoading: any) => {
  setIsLoading(true);
  DispensingOrdersService.getCars(customerId).then((response: Array<Car>) => {
      setIsLoading(false);
      setCars(response.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ value: c.code, label: c.name })));
  });
};


const getCustomer = (customerId: number, setCustomer: any, setIsLoading: any) => {
  setIsLoading(true);
  SaleOrderService.getCustomer(customerId).then(c => {
    setCustomer(c);
    setIsLoading(false);
  });
}

const getProducts = (loadingProviderId: number, setProducts: any) => {
  SaleOrderService.getProducts(loadingProviderId).then((res) => {
    setProducts(res.sort((a, b) => !a.exciseType || !b.exciseType ? 0 : a.exciseType?.localeCompare(b.exciseType)));
  });
}

const getProviders = (setProviders: any, setIsLoading: any) => {
  setIsLoading(true);
  SaleOrderService.getLoadingProviders()
    .then((res) => {
      setProviders(res.sort((a, b) => a.name.localeCompare(b.name)).map(p => ({ value: p.id, label: p.name, objectId: p.objectId, searchCode: p.searchCode })));
      setIsLoading(false);
    }
  );
}

const getCarriers = (customerSearchCode: string, setCarriers: any, setCars: any, setDrivers: any, form: TransportForm, setForm: any, setIsLoading: any) => {
  setIsLoading(true);
  DispensingOrdersService.getCarriers().then((response: Array<Carrier>) => {
    const matching = response.find(c => c.searchCode === customerSearchCode);
    if (matching) {
      setForm({...form, carrier: { value: matching.id, label: matching.name }})
      getCars(matching.id, setCars, setIsLoading);
      getDrivers(matching.id, setDrivers, setIsLoading);
    }
    setCarriers(response.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ value: c.id, label: c.name })));
    setIsLoading(false);
  });
}